import React, { ReactElement, useEffect, useRef, useState } from "react"
import { AutoSizer, List, ListRowProps, WindowScroller } from "react-virtualized"
import Select, { SingleValue } from "react-select"
import { Button, Modal } from "@wfp/ui"
import { iconAddOutline } from "@wfp/icons"
import { IChoiceOption, IChoices, IChoice, ListOptionGroup } from "interfaces/xlsForm"
import useCommon from "hooks/useCommon"
import OverflowText from "components/Shared/OverflowText/OverflowText"
import ChoiceForm from "components/Household/Form/ChoiceForm"

interface Props {
  xlsFormId: number
  permissions: boolean
  list: ListOptionGroup[]
  saveChoice: (payload: IChoiceOption, onClose: () => void) => Promise<void>
  loadingSaveChoice: boolean
}

const initialChoiceForm: IChoiceOption = {
  name: "",
  label: "",
  choiceListId: 0,
}

const ChoicesList = ({ xlsFormId, permissions, list, saveChoice, loadingSaveChoice }: Props): ReactElement => {
  const listReference = useRef<List>(null)
  const { showSnackBar } = useCommon()

  const [currentList, setCurrentList] = useState<SingleValue<IChoices>>(null)
  const [filteredList, setFilteredList] = useState<IChoice[]>([])
  const [openChoiceModal, setOpenChoiceModal] = useState<boolean>(false)
  const [openChoiceListModal, setOpenChoiceListModal] = useState<boolean>(false)
  const [choiceForm, setChoiceForm] = useState<IChoiceOption>(initialChoiceForm)

  const onClose = (): void => {
    openChoiceModal && setOpenChoiceModal(false)
    openChoiceListModal && setOpenChoiceListModal(false)
    setChoiceForm(initialChoiceForm)
  }

  const submitChoice = async (): Promise<void> => {
    if (!choiceForm.choiceListId || !choiceForm.name || !choiceForm.label) {
      showSnackBar("Choice list, name and label are required", "warning")
      return
    }

    await saveChoice(choiceForm, onClose)
  }

  const customStyles = {
    option: (styles, { data, isFocused }) => ({
      ...styles,
      backgroundColor: isFocused ? "#EBF3FB" : "#fff",
      color: !data.choices?.length ? "var(--support-01)" : "#000",
    }),
  }

  useEffect(() => {
    list &&
      setFilteredList(
        list.flatMap(item => item.options)?.find(option => option.listId === currentList?.listId)?.choices ?? [],
      )
  }, [list])

  const rowRenderer = ({ key, style, field }): ReactElement => {
    return (
      <div
        key={`${key}-${field.choiceName}`}
        style={{ ...style }}
        className="flex border-solid border-b border-gray-200 align-center w-full"
      >
        <div className="w-full grid grid-cols-3 gap-2 grid-flow-col" style={{ gridTemplateColumns: "3fr" }}>
          <div className="col-span-2">
            <div className="font-bold text-sm">{field.choiceName}</div>
            <OverflowText dark>{field.choiceLabel}</OverflowText>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-3">
      <div className="flex items-center justify-between mb-3">
        <Select
          className="w-1/2"
          placeholder="Select choice list"
          styles={customStyles}
          options={list || []}
          getOptionLabel={option => option.listName}
          getOptionValue={option => option.listId?.toString()}
          isSearchable
          value={list?.flatMap(item => item.options)?.find(option => option.listId === currentList?.listId)}
          formatOptionLabel={option => (
            <div className="flex items-center justify-between">
              <div>{option.listName}</div>
              <div className="text-xs">{option.choices?.length}</div>
            </div>
          )}
          onChange={item => {
            setCurrentList(item)
            item?.listId
              ? setFilteredList(
                  list.flatMap(el => el.options)?.find(option => option.listId === item?.listId)?.choices ?? [],
                )
              : setFilteredList([])
          }}
          isClearable
        />

        {permissions && (
          <div>
            <Button
              kind="secondary"
              icon={iconAddOutline}
              iconDescription="Add new choice"
              className="mr-2"
              onClick={() => {
                setOpenChoiceModal(prev => !prev)
              }}
            >
              Add new choice
            </Button>
          </div>
        )}
      </div>

      <WindowScroller scrollElement={window}>
        {({ height, scrollTop }) => (
          <AutoSizer disableHeight size={`size-${filteredList?.length}`}>
            {({ width }) => (
              <List
                ref={listReference}
                autoHeight
                width={width}
                height={height}
                rowCount={filteredList?.length ?? 0}
                rowHeight={45}
                scrollTop={scrollTop - 100}
                rowRenderer={({ key, index, style }: ListRowProps) =>
                  rowRenderer({ key, style, field: filteredList?.[index] })
                }
              />
            )}
          </AutoSizer>
        )}
      </WindowScroller>

      <Modal
        lazyLoad
        open={openChoiceModal}
        modalLabel="New choice"
        primaryButtonText={loadingSaveChoice ? "Saving..." : "Confirm"}
        secondaryButtonText="Cancel"
        onRequestSubmit={submitChoice}
        onRequestClose={onClose}
        className="modal-lg"
        primaryButtonDisabled={loadingSaveChoice}
      >
        <ChoiceForm xlsFormId={xlsFormId} form={choiceForm} setForm={setChoiceForm} currentList={currentList} />
      </Modal>
    </div>
  )
}

export default ChoicesList
